import { supabase } from './supabase';
import { allowedSetNumbers, getSessionSetLimit } from '../data/examCatalog';

export interface QuestionMaster {
  id?: number;
  exam_id: number;
  category_id: number;
  question: string;
  option1: string;
  option2: string;
  option3: string;
  option4: string;
  option5: string;
  option6: string;
  total_option: number;
  correct_ans: string;
  explaination: string;
  set?: number | null;
  created_at?: string;
}

/**
 * Fetch a single question by ID
 */
export async function fetchQuestionById(id: number): Promise<{ success: boolean; data?: QuestionMaster; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('question_master')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Supabase fetch by ID error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Fetch question by ID error:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Convert exam type string to exam_id number
 * This maps the exam types from AddQuestionPage to database exam_ids
 */
export function getExamIdFromType(examType: string): number {
  const examTypeMap: Record<string, number> = {
    'saa': 1,
    'sap': 2,
    'ccp': 3,
    'dva': 4,
    'sysdesign': 5,
    'dsa': 6,
    'python': 7,
    'dotnet': 8,
    'aiml': 9,
  };
  return examTypeMap[examType] || 1;
}

/**
 * Convert category ID string to category_id number
 * Handles both numeric (1, 2, etc.) and custom (custom_1, etc.) formats
 */
export function getCategoryIdFromString(categoryId: string): number {
  // If it's already numeric, return as number
  const numericId = parseInt(categoryId);
  console.log('getCategoryIdFromString input:', categoryId, 'parsed:', numericId);
  if (!isNaN(numericId)) {
    return numericId;
  }
  
  // If it's a custom category like "saa_1", extract the number
  const parts = categoryId.split('_');
  if (parts.length > 1) {
    const lastPart = parts[parts.length - 1];
    const lastNumeric = parseInt(lastPart);
    if (!isNaN(lastNumeric)) {
      return lastNumeric + 1000; // Add offset to avoid conflict
    }
  }
  
  // Default fallback
  return 1;
}

/**
 * Format correct answer from array or number to comma-separated string
 */
export function formatCorrectAnswer(correctAnswer: number | number[]): string {
  if (Array.isArray(correctAnswer)) {
    return correctAnswer.map(a => String(a + 1)).join(',');
  }
  return String(correctAnswer + 1);
}

/**
 * Insert a new question into question_master table
 */
export async function insertQuestion(data: {
  examId: string;
  categoryId: string;
  question: string;
  options: string[];
  correctAnswer: number | number[];
  explaination: string;
  set?: number | null;
}): Promise<{ success: boolean; error?: string; data?: QuestionMaster }> {
  try {
    const examId = getExamIdFromType(data.examId);
    const categoryId = getCategoryIdFromString(data.categoryId);
    const correctAns = formatCorrectAnswer(data.correctAnswer);
    const options = data.options;
    
    const questionData: QuestionMaster = {
      exam_id: examId,
      category_id: categoryId,
      question: data.question,
      option1: options[0] || '',
      option2: options[1] || '',
      option3: options[2] || '',
      option4: options[3] || '',
      option5: options[4] || '',
      option6: options[5] || '',
      total_option: options.length,
      correct_ans: correctAns,
      explaination: data.explaination,
    };
    if (data.set !== undefined && data.set !== null) {
      questionData.set = data.set;
    }

    const { data: insertedData, error } = await supabase
      .from('question_master')
      .insert(questionData)
      .select()
      .single();

    if (error) {
      console.error('Supabase insert error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: insertedData };
  } catch (error) {
    console.error('Insert question error:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Fetch all questions from question_master table
 */
export async function fetchAllQuestions(): Promise<{ success: boolean; data?: QuestionMaster[]; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('question_master')
      .select('*')
      .order('id', { ascending: false });

    if (error) {
      console.error('Supabase fetch error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Fetch questions error:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Fetch questions by category_id
 */
export async function fetchQuestionsByCategory(categoryId: number): Promise<{ success: boolean; data?: QuestionMaster[]; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('question_master')
      .select('*')
      .eq('category_id', categoryId)
      .order('id', { ascending: false });

    if (error) {
      console.error('Supabase fetch by category error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Fetch questions by category error:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Update an existing question
 */
export async function updateQuestion(
  id: number, 
  data: {
    examId?: string;
    categoryId?: string;
    question?: string;
    options?: string[];
    correctAnswer?: number | number[];
    explanation?: string;
    set?: number | null;
  }
): Promise<{ success: boolean; error?: string }> {
  try {
    const updateData: Partial<QuestionMaster> = {};
    
    if (data.examId !== undefined) {
      updateData.exam_id = getExamIdFromType(data.examId);
    }
    if (data.categoryId !== undefined) {
      updateData.category_id = getCategoryIdFromString(data.categoryId);
    }
    if (data.question !== undefined) {
      updateData.question = data.question;
    }
    if (data.options !== undefined) {
      updateData.option1 = data.options[0] || '';
      updateData.option2 = data.options[1] || '';
      updateData.option3 = data.options[2] || '';
      updateData.option4 = data.options[3] || '';
      updateData.option5 = data.options[4] || '';
      updateData.option6 = data.options[5] || '';
      updateData.total_option = data.options.length;
    }
    if (data.correctAnswer !== undefined) {
      updateData.correct_ans = formatCorrectAnswer(data.correctAnswer);
    }
    if (data.explanation !== undefined) {
      updateData.explaination = data.explanation;
    }
    if (data.set !== undefined) {
      updateData.set = data.set;
    }

    // Log for debugging
    console.log('Updating question ID:', id);
    console.log('Update data:', updateData);

    // Use select without single() to avoid issues
    const { data: _updatedData, error } = await supabase
      .from('question_master')
      .update(updateData)
      .eq('id', id)
      .select();

    console.log('Supabase response - data:', _updatedData, 'error:', error);

    if (error) {
      console.error('Supabase update error:', error);
      return { success: false, error: error.message };
    }

    // If no error but no data returned, check if update actually happened
    if (!_updatedData || _updatedData.length === 0) {
      console.error('No data returned from update - question may not exist');
      return { success: false, error: 'Question not found or update failed' };
    }

    console.log('Update succeeded, updated row:', _updatedData[0]);
    return { success: true };
  } catch (error) {
    console.error('Update question error:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Delete a question
 */
/** Set (or clear, with null) the `set` of many questions at once. */
export async function updateQuestionsSet(ids: number[], set: number | null): Promise<{ success: boolean; error?: string }> {
  if (ids.length === 0) return { success: true };
  try {
    const { error } = await supabase.from('question_master').update({ set }).in('id', ids);
    if (error) {
      console.error('Supabase bulk set update error:', error);
      return { success: false, error: error.message };
    }
    return { success: true };
  } catch (error) {
    return { success: false, error: String(error) };
  }
}

export async function deleteQuestion(id: number): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('question_master')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Supabase delete error:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Delete question error:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Fetch questions and categories for an exam in a single API call
 * Returns both questions and grouped categories with counts
 */
export async function fetchExamData(examId: number): Promise<{
  success: boolean;
  questions?: QuestionMaster[];
  categories?: { category_id: number; label: string; count: number }[];
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('question_master')
      .select('id, exam_id, category_id, question, option1, option2, option3, option4, option5, option6, total_option, correct_ans, explaination, set')
      .eq('exam_id', examId)
      .order('id', { ascending: true });

    if (error) {
      console.error('Supabase fetch error:', error);
      return { success: false, error: error.message };
    }

    // A learner limited to N sets (user_access.no_of_sets) only gets the questions of those sets
    const limit = getSessionSetLimit();
    let rows = data || [];
    if (limit) {
      const allowed = new Set(allowedSetNumbers(Array.from(new Set(rows.map(r => r.set).filter((n): n is number => typeof n === 'number'))), limit));
      rows = rows.filter(r => typeof r.set === 'number' && allowed.has(r.set));
    }

    // Remove duplicates and group by category
    const uniqueQuestions = rows.reduce((acc: QuestionMaster[], q) => {
      if (!acc.find(existing => existing.id === q.id)) {
        acc.push(q);
      }
      return acc;
    }, []);

    // Build categories from the questions
    const categoryMap = new Map<number, number>();
    uniqueQuestions.forEach(q => {
      const catId = q.category_id;
      categoryMap.set(catId, (categoryMap.get(catId) || 0) + 1);
    });

    const categories = Array.from(categoryMap.entries()).map(([categoryId, count]) => ({
      category_id: categoryId,
      label: getCategoryLabel(examId, categoryId),
      count
    }));

    return { 
      success: true, 
      questions: uniqueQuestions,
      categories 
    };
  } catch (error) {
    console.error('Fetch exam data error:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Get category information for a specific exam from the database
 * Returns unique categories with question counts
 */
export async function fetchCategoriesByExam(examId: number): Promise<{
  success: boolean;
  data?: { category_id: number; label: string; count: number }[];
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('question_master')
      .select('category_id, question')
      .eq('exam_id', examId);

    if (error) {
      console.error('Supabase fetch categories error:', error);
      return { success: false, error: error.message };
    }

    // Group by category_id and count questions
    const categoryMap = new Map<number, number>();
    (data || []).forEach((q) => {
      const catId = q.category_id;
      categoryMap.set(catId, (categoryMap.get(catId) || 0) + 1);
    });

    // Create category info with labels based on exam type
    const categories = Array.from(categoryMap.entries()).map(([categoryId, count]) => {
      const label = getCategoryLabel(examId, categoryId);
      return { category_id: categoryId, label, count };
    });

    return { success: true, data: categories };
  } catch (error) {
    console.error('Fetch categories by exam error:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Bulk insert multiple questions into question_master table
 */
export async function bulkInsertQuestions(questions: QuestionMaster[]): Promise<{ success: boolean; error?: string; insertedCount?: number }> {
  try {
    if (questions.length === 0) {
      return { success: false, error: 'No questions to insert' };
    }

    let totalInserted = 0;
    const errors: string[] = [];
    const batchSize = 10;
    const questionDelay = 10000; // 10 seconds between each question
    const batchDelay = 300000; // 5 minutes between batches

    // Insert in batches of 10
    for (let i = 0; i < questions.length; i++) {
      // Add delay between each insert to avoid rate limiting
      if (i > 0) {
        // Check if we're starting a new batch
        if ((i) % batchSize === 0) {
          console.log(`Batch complete. Waiting ${batchDelay / 1000} seconds before next batch...`);
          await new Promise(resolve => setTimeout(resolve, batchDelay));
        } else {
          await new Promise(resolve => setTimeout(resolve, questionDelay));
        }
      }

      console.log(`Inserting question ${i + 1}/${questions.length}`);

      const { data, error } = await supabase
        .from('question_master')
        .insert(questions[i])
        .select();

      if (error) {
        console.error(`Insert error for question ${i + 1}:`, error);
        errors.push(`Q${i + 1}: ${error.message}`);
        continue;
      }

      totalInserted++;
    }

    console.log(`Total inserted: ${totalInserted}/${questions.length}`);
    if (errors.length > 0) {
      console.error('Insert errors:', errors);
    }

    if (totalInserted === 0 && questions.length > 0) {
      return { success: false, error: errors.join('; ') || 'Failed to insert questions' };
    }

    return { 
      success: totalInserted > 0, 
      insertedCount: totalInserted,
      error: errors.length > 0 ? `Imported ${totalInserted} questions. ${errors.length} failed.` : undefined
    };
  } catch (error) {
    console.error('Bulk insert questions error:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Get category label based on exam type and category_id
 * (without the category number prefix)
 */
export function getCategoryLabel(examId: number, categoryId: number): string {
  const examCategories: Record<number, Record<number, string>> = {
    // SAP-C02 (exam_id: 2)
    2: {
      101: 'Design for Organizational Complexity',
      102: 'Design for New Solutions',
      103: 'Migration Planning',
      104: 'Cost Optimization',
      105: 'Security & Compliance',
      106: 'Network & Hybrid Architecture',
      107: 'Multi-Account Strategy',
      108: 'Business Continuity',
      109: 'Deployment & Operations',
      110: 'Data Protection & Privacy',
    },
    // SAA-C03 (exam_id: 1)
    1: {
      1: 'Hybrid DNS & Failover',
      2: 'Compute & Auto Scaling',
      3: 'Security & IAM',
      4: 'Storage & Data Migration',
      5: 'Networking & VPC',
      6: 'Databases & Caching',
      7: 'Serverless & Lambda',
      8: 'High Availability & DR',
      9: 'Cost Optimization',
      10: 'Monitoring & Governance',
      11: 'Multi-Account & Org',
      12: 'Analytics & Big Data',
      13: 'Containers & ECS/EKS',
      14: 'Migration & Hybrid Cloud',
      15: 'Advanced Networking',
      16: 'Exam Simulation Set',
    },
    // CCP (exam_id: 3)
    3: {
      201: 'Cloud Concepts',
      202: 'AWS Security',
      203: 'Technology',
      204: 'Billing & Pricing',
    },
    // DVA (exam_id: 4)
    4: {
      301: 'Development with AWS Services',
      302: 'Security',
      303: 'Deployment',
      304: 'Troubleshooting & Optimization',
    },
  };

  const examCats = examCategories[examId];
  if (examCats && examCats[categoryId]) {
    return examCats[categoryId];
  }

  return `Category ${categoryId}`;
}
/* ───────────── Set-wise exam helpers (question_master.set) ───────────── */

export interface ExamSetSummary {
  set: number;
  count: number;
}

/** A question_master row converted to what the exam UI needs. `ans` holds 0-based indexes of every correct option. */
export interface SetExamQuestion {
  id: number;
  q: string;
  opts: string[];
  ans: number[];
  explain: string;
}

export function toSetExamQuestion(row: QuestionMaster): SetExamQuestion {
  const all = [row.option1, row.option2, row.option3, row.option4, row.option5, row.option6];
  const total = row.total_option > 0 ? Math.min(row.total_option, 6) : 4;
  const opts = all.slice(0, total).map(o => o ?? '');
  const ans = String(row.correct_ans ?? '')
    .split(',')
    .map(x => parseInt(x.trim(), 10) - 1)
    .filter(n => !isNaN(n) && n >= 0 && n < opts.length);
  return { id: row.id ?? 0, q: row.question, opts, ans, explain: row.explaination || '' };
}

/** Distinct sets of an exam with the number of questions in each, ordered by set number. */
export async function fetchExamSets(examId: number): Promise<{ success: boolean; sets?: ExamSetSummary[]; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('question_master')
      .select('set')
      .eq('exam_id', examId)
      .not('set', 'is', null);

    if (error) {
      console.error('Supabase fetch sets error:', error);
      return { success: false, error: error.message };
    }

    const counts = new Map<number, number>();
    (data || []).forEach((r: { set: number | null }) => {
      if (r.set !== null && r.set !== undefined) counts.set(r.set, (counts.get(r.set) || 0) + 1);
    });
    const sets = Array.from(counts.entries())
      .map(([set, count]) => ({ set, count }))
      .sort((a, b) => a.set - b.set);
    return { success: true, sets };
  } catch (error) {
    console.error('Fetch sets error:', error);
    return { success: false, error: String(error) };
  }
}

/** All questions of one set, in a stable order (by id). */
export async function fetchQuestionsBySet(examId: number, setNo: number): Promise<{ success: boolean; questions?: SetExamQuestion[]; error?: string; locked?: boolean }> {
  try {
    // A learner limited to N sets (user_access.no_of_sets) cannot load the questions of the other sets
    if (getSessionSetLimit()) {
      const all = await fetchExamSets(examId);
      if (!all.success || !all.sets) return { success: false, error: all.error || 'Could not check set access.' };
      if (!allowedSetNumbers(all.sets.map(s => s.set)).includes(setNo)) {
        return { success: false, locked: true, error: 'This set is not included in your plan.' };
      }
    }
    const { data, error } = await supabase
      .from('question_master')
      .select('id, exam_id, category_id, question, option1, option2, option3, option4, option5, option6, total_option, correct_ans, explaination, set')
      .eq('exam_id', examId)
      .eq('set', setNo)
      .order('id', { ascending: true });

    if (error) {
      console.error('Supabase fetch by set error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, questions: (data || []).map(r => toSetExamQuestion(r as QuestionMaster)) };
  } catch (error) {
    console.error('Fetch questions by set error:', error);
    return { success: false, error: String(error) };
  }
}
